import React, { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";

import Config from "../../Config";
import { get_paymentId } from "../../../store/Actions/authActions";
import { walletReturnUrl } from "../../../utils/walletOrder";
import {
  brandLabel,
  cardExpiry,
  formatEuro,
  HISTORY_PAGE,
  historyRows,
  MIN_TOPUP,
  parseTopup,
  TOPUP_AMOUNTS,
} from "../../../utils/wallet";
import CardForm from "../Checkout/Recap/CardForm";
import { Sprite as RecapSprite } from "../Checkout/Recap/icons";
import { Icon, Sprite } from "../Orders/icons";
import "../Checkout/Recap/recap.css";
import "../Orders/orders.css";
import "./payment.css";

/**
 * Paiement et portefeuille — design « Paiement et portefeuille.dc.html ».
 *
 * Ce que le serveur sait faire, et donc ce que la page montre :
 *  - portefeuille : solde et historique (GET /customer/wallet), recharge
 *    Bancontact (GET /customer/secret puis Stripe, retour sur
 *    /wallet-success qui renvoie ici) ;
 *  - cartes : liste, ajout (Stripe Elements → POST /customer/payment_methods),
 *    suppression (DELETE, refusée tant qu'une commande en cours l'utilise).
 *
 * Laissé de côté faute d'appui côté serveur (ni sur le mobile) : la carte
 * « par défaut », les bonus de recharge, Apple Pay et la carte comme moyen
 * de recharge.
 */

const headersOf = (authUser) => ({
  uid: authUser?.user_uid,
  client: authUser?.user_client,
  "access-token": authUser?.user_access_token,
});

const storedLaundryId = () => {
  const n = Number(localStorage.getItem("laundry_id"));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const serverMessage = (error) => {
  const data = error?.response?.data;
  const message = data?.message ?? data?.errors?.[0] ?? data?.error;
  if (typeof message === "string") return message;
  if (message && typeof message === "object") return Object.values(message).flat().join(" ");
  return null;
};

export function PaymentWallet({ authUser, cards, profile, dispatch }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const headers = headersOf(authUser);

  const [balance, setBalance] = useState(null);
  const [walletFailed, setWalletFailed] = useState(false);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const [topupOpen, setTopupOpen] = useState(false);
  const [preset, setPreset] = useState(50);
  const [custom, setCustom] = useState("");
  /* Un montant saisi remplace le montant proposé sélectionné. */
  const amount = custom.trim() ? parseTopup(custom) : preset;
  const belowMinimum = amount < MIN_TOPUP;
  const [holderName, setHolderName] = useState("");
  const [paying, setPaying] = useState(false);
  const [topupError, setTopupError] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [cardHolder, setCardHolder] = useState("");
  const [cardFields, setCardFields] = useState({});
  const [cardError, setCardError] = useState(null);
  const [savingCard, setSavingCard] = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  const flash = (message) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  };

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const loadWallet = useCallback(
    () =>
      Axios({ url: `${Config.apiURL}/customer/wallet`, method: "GET", headers })
        .then((response) => {
          setBalance(Number(response?.data?.wallet?.amount) || 0);
          setEntries(response?.data?.wallet?.wallet_entries || []);
          setWalletFailed(false);
        })
        /* Pas de faux « 0,00 € » : on garde « — » et on le dit. */
        .catch(() => setWalletFailed(true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authUser]
  );

  useEffect(() => {
    loadWallet();
    dispatch(get_paymentId());
  }, [loadWallet, dispatch]);

  /* Stripe exige le nom du titulaire pour Bancontact : celui du profil. */
  const profileName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim();
  const payerName = (holderName || profileName).trim();

  const topUp = async () => {
    if (paying || belowMinimum) return;
    if (!payerName) {
      setTopupError(t("Enter the account holder's name."));
      return;
    }
    setPaying(true);
    setTopupError(null);
    /* /wallet-success renvoie ici une fois la banque passée. */
    localStorage.setItem("redirect", "/paymentMethod");

    let secret;
    try {
      const response = await Axios({
        url: `${Config.apiURL}/customer/secret`,
        method: "GET",
        headers,
        params: { amount, laundry_place_id: storedLaundryId() },
      });
      secret = response?.data?.client_secret;
    } catch (error) {
      setPaying(false);
      setTopupError(serverMessage(error) || t("Server is not responding at the moment, please try later"));
      return;
    }

    if (!stripe || !secret) {
      setPaying(false);
      setTopupError(t("Server is not responding at the moment, please try later"));
      return;
    }

    const { error } = await stripe.confirmBancontactPayment(secret, {
      payment_method: { billing_details: { name: payerName } },
      return_url: walletReturnUrl(),
    });
    /* Sans erreur, Stripe a déjà quitté la page pour la banque. */
    if (error) {
      setTopupError(error.message);
      setPaying(false);
    }
  };

  const cardComplete = cardFields.number && cardFields.expiry && cardFields.cvc && cardHolder.trim();

  const saveCard = async () => {
    if (savingCard || !cardComplete || !stripe || !elements) return;
    setSavingCard(true);
    setCardError(null);

    const { token, error } = await stripe.createToken(elements.getElement(CardNumberElement), {
      name: cardHolder.trim(),
    });
    if (error) {
      setCardError(error.message);
      setSavingCard(false);
      return;
    }

    try {
      await Axios({
        url: `${Config.apiURL}/customer/payment_methods`,
        method: "POST",
        headers,
        data: { token: token.id, laundry_place_id: storedLaundryId() },
      });
    } catch (err) {
      setCardError(serverMessage(err) || t("This card could not be added."));
      setSavingCard(false);
      return;
    }

    await dispatch(get_paymentId());
    setSavingCard(false);
    setAddOpen(false);
    setCardHolder("");
    setCardFields({});
    flash(t("Card added"));
  };

  const deleteCard = async (card) => {
    if (deletingId) return;
    setDeletingId(card.id);
    setDeleteError(null);
    try {
      await Axios({ url: `${Config.apiURL}/customer/payment_methods/${card.id}`, method: "DELETE", headers });
    } catch (error) {
      const message = serverMessage(error);
      setDeleteError({ id: card.id, message: message ? t(message) : t("This card could not be deleted.") });
      setDeletingId(null);
      return;
    }
    await dispatch(get_paymentId());
    setDeletingId(null);
    setConfirmId(null);
    flash(t("Card deleted"));
  };

  const savedCards = (cards || []).filter((card) => card?.id);
  const rows = historyRows(entries, filter);
  const shown = showAll ? rows : rows.slice(0, HISTORY_PAGE);

  return (
    <div className="wosh-ord wosh-pay">
      <Sprite />
      <RecapSprite />
      <main className="wosh-pay__page">
        <div className="wosh-ord__titles">
          <div className="wosh-ord__eyebrow">{t("My account")}</div>
          <h1 className="wosh-ord__title">{t("Payment & wallet")}</h1>
        </div>

        {/* ── Portefeuille ─────────────────────────────── */}
        <section className="wosh-pay__wallet">
          <div className="wosh-pay__wallet-head">
            <div className="wosh-pay__wallet-text">
              <div className="wosh-pay__wallet-eyebrow">
                <Icon name="wallet-outline" />
                {t("WOSH wallet")}
              </div>
              <div className="wosh-pay__balance">{balance === null ? "—" : formatEuro(balance)}</div>
              <div className="wosh-pay__wallet-sub">
                {walletFailed && balance === null
                  ? t("Balance unavailable right now. Try again in a moment.")
                  : t("Used first on every order, before your card.")}
              </div>
            </div>
            {!topupOpen && (
              <button type="button" className="wosh-pay__topup-btn" onClick={() => setTopupOpen(true)}>
                <Icon name="add" />
                {t("Top up")}
              </button>
            )}
          </div>

          {topupOpen && (
            <div className="wosh-pay__topup">
              <div className="wosh-pay__topup-head">
                <span className="wosh-pay__topup-title">{t("Top up your wallet")}</span>
                <button
                  type="button"
                  className="wosh-pay__close"
                  aria-label={t("Close")}
                  disabled={paying}
                  onClick={() => {
                    setTopupOpen(false);
                    setTopupError(null);
                  }}
                >
                  <Icon name="close" />
                </button>
              </div>

              <div className="wosh-pay__amounts">
                {TOPUP_AMOUNTS.map((value) => (
                  <button
                    type="button"
                    key={value}
                    className={`wosh-pay__choice${!custom.trim() && preset === value ? " wosh-pay__choice--on" : ""}`}
                    aria-pressed={!custom.trim() && preset === value}
                    onClick={() => {
                      setPreset(value);
                      setCustom("");
                    }}
                  >
                    <span className="wosh-pay__amount">€{value}</span>
                  </button>
                ))}
              </div>

              <label className="wosh-pay__group">
                <span className="wosh-pay__group-label">{t("Or enter an amount")}</span>
                <span className={`wosh-pay__custom${custom.trim() ? " wosh-pay__custom--on" : ""}`}>
                  <span className="wosh-pay__custom-prefix">€</span>
                  <input
                    inputMode="decimal"
                    value={custom}
                    placeholder="0,00"
                    aria-invalid={Boolean(custom.trim()) && belowMinimum}
                    onChange={(event) => setCustom(event.target.value)}
                  />
                </span>
                {custom.trim() && belowMinimum && (
                  <span className="wosh-pay__hint">
                    {t("The minimum is {{amount}}, set by our payment provider.", { amount: formatEuro(MIN_TOPUP) })}
                  </span>
                )}
              </label>

              <div className="wosh-pay__group">
                <span className="wosh-pay__group-label">{t("Pay with")}</span>
                <div className="wosh-pay__methods">
                  <span className="wosh-pay__choice wosh-pay__choice--on wosh-pay__method">
                    <span>Bancontact</span>
                    <Icon name="checkmark-sharp" />
                  </span>
                </div>
              </div>

              {!profileName && (
                <label className="wosh-pay__group">
                  <span className="wosh-pay__group-label">{t("Account holder")}</span>
                  <input
                    className="wosh-pay__input"
                    value={holderName}
                    autoComplete="name"
                    placeholder={t("First and last name")}
                    onChange={(event) => setHolderName(event.target.value)}
                  />
                </label>
              )}

              {topupError && <div className="wosh-pay__error">{topupError}</div>}

              <button type="button" className="wosh-pay__cta" onClick={topUp} disabled={paying || belowMinimum}>
                {paying && <span className="wosh-po__spin wosh-po__spin--light" />}
                {belowMinimum ? t("Select an amount") : t("Pay {{amount}}", { amount: formatEuro(amount) })}
              </button>
            </div>
          )}
        </section>

        {/* ── Cartes ───────────────────────────────────── */}
        <section className="wosh-pay__card">
          <div className="wosh-pay__card-head">
            <div className="wosh-pay__card-titles">
              <span className="wosh-pay__h2">{t("Saved cards")}</span>
              <span className="wosh-pay__secure">
                <Icon name="lock-closed" />
                {t("Encrypted and stored by Stripe.")}
              </span>
            </div>
            {!addOpen && (
              <button type="button" className="wosh-pay__add" onClick={() => setAddOpen(true)}>
                <Icon name="add" />
                {t("Add card")}
              </button>
            )}
          </div>

          {addOpen && (
            <div className="wosh-pay__add-form">
              <CardForm
                t={t}
                holder={cardHolder}
                onHolder={setCardHolder}
                onFieldChange={(name, event) => {
                  setCardFields((current) => ({ ...current, [name]: event.complete }));
                  setCardError(event.error ? event.error.message : null);
                }}
                error={cardError}
              />
              <div className="wosh-pay__add-actions">
                <button
                  type="button"
                  className="wosh-pay__ghost"
                  disabled={savingCard}
                  onClick={() => {
                    setAddOpen(false);
                    setCardError(null);
                  }}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="wosh-pay__save"
                  disabled={!cardComplete || savingCard}
                  onClick={saveCard}
                >
                  {savingCard && <span className="wosh-po__spin wosh-po__spin--light" />}
                  {t("Save card")}
                </button>
              </div>
            </div>
          )}

          <div className="wosh-pay__cards">
            {savedCards.map((card) => {
              const confirming = confirmId === card.id;
              const error = deleteError?.id === card.id ? deleteError.message : null;
              return (
                <div key={card.id} className={`wosh-pay__row${confirming ? " wosh-pay__row--danger" : ""}`}>
                  <span className="wosh-pay__brand">{brandLabel(card.brand)}</span>
                  <span className="wosh-pay__row-text">
                    <span className="wosh-pay__last4">•••• {card.last4}</span>
                    {cardExpiry(card) && (
                      <span className="wosh-pay__exp">{t("Expires {{date}}", { date: cardExpiry(card) })}</span>
                    )}
                    {error && <span className="wosh-pay__row-error">{error}</span>}
                  </span>
                  {confirming ? (
                    <span className="wosh-pay__confirm">
                      <span className="wosh-pay__confirm-q">{t("Delete?")}</span>
                      <button
                        type="button"
                        className="wosh-pay__ghost wosh-pay__ghost--sm"
                        disabled={deletingId === card.id}
                        onClick={() => {
                          setConfirmId(null);
                          setDeleteError(null);
                        }}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="button"
                        className="wosh-pay__danger"
                        disabled={deletingId === card.id}
                        onClick={() => deleteCard(card)}
                      >
                        {t("Delete")}
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="wosh-pay__trash"
                      aria-label={t("Delete card")}
                      onClick={() => {
                        setConfirmId(card.id);
                        setDeleteError(null);
                      }}
                    >
                      <Icon name="trash-outline" />
                    </button>
                  )}
                </div>
              );
            })}
            {!savedCards.length && (
              <div className="wosh-pay__empty">
                <Icon name="card-outline" />
                {t("No saved card. Add one to pay for your orders.")}
              </div>
            )}
          </div>
        </section>

        {/* ── Historique ───────────────────────────────── */}
        <section className="wosh-pay__card wosh-pay__history">
          <div className="wosh-pay__card-head">
            <span className="wosh-pay__h2">{t("History")}</span>
            <div className="wosh-pay__filters" role="tablist">
              {[
                ["all", t("All")],
                ["topup", t("Top-ups")],
                ["order", t("Orders")],
              ].map(([id, label]) => (
                <button
                  type="button"
                  role="tab"
                  key={id}
                  aria-selected={filter === id}
                  className={`wosh-pay__filter${filter === id ? " wosh-pay__filter--on" : ""}`}
                  onClick={() => {
                    setFilter(id);
                    setShowAll(false);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {shown.length ? (
            <div className="wosh-pay__tx-list">
              {shown.map((row, i) => (
                <div className="wosh-pay__tx" key={row.id ?? i}>
                  <span className={`wosh-pay__tx-icon${row.credit ? " wosh-pay__tx-icon--credit" : ""}`}>
                    <Icon name={row.credit ? "wallet-outline" : "basket-outline"} />
                  </span>
                  <span className="wosh-pay__tx-text">
                    <span className="wosh-pay__tx-label">{row.label || (row.credit ? t("Top up") : t("Orders"))}</span>
                    <span className="wosh-pay__tx-date">{row.date}</span>
                  </span>
                  <span className={`wosh-pay__tx-amount${row.credit ? " wosh-pay__tx-amount--credit" : ""}`}>
                    {row.amount}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="wosh-pay__empty">{t("No transactions yet")}</div>
          )}

          {!showAll && rows.length > HISTORY_PAGE && (
            <button type="button" className="wosh-pay__more" onClick={() => setShowAll(true)}>
              {t("See full history")}
            </button>
          )}
        </section>
      </main>

      {toast && (
        <div className="wosh-pay__toast" role="status">
          <Icon name="checkmark-circle" />
          {toast}
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
  cards: state?.auth?.paymentId,
  profile: state?.auth?.profileUser,
});

export default connect(mapStateToProps)(PaymentWallet);
